// ViewerStateStore
//
// Source-of-truth for all per-uniqueId visualization state that must
// survive tile unload, LOD swap, and re-materialization. Keyed by
// uniqueId — NOT by InstancedMesh+index — because the (mesh, index)
// pair changes every time a tile re-materializes after an unload.
// uniqueId is the only identity that persists across the streaming
// lifecycle.
//
// === DESIGN GOALS ===
//
// 1. SCALE TO 10,000+ UIDS PER OPERATION. Tree section toggles
//    affect entire categories of components (all walls, all rebar,
//    etc). Operations must be O(N) not O(N × tiles), and must emit
//    EXACTLY ONE event per user action regardless of how many uids
//    it touches.
//
// 2. UI SOURCE OF TRUTH. UI eye-icons read directly from this store
//    via isGroupHidden(groupId). No parallel "is this section
//    hidden" bool drifting out of sync.
//
// 3. ISOLATE/HIDE COMPOSITION CLEAN. The two are orthogonal:
//      isVisible(uid) =
//        (isolated == null || isolated.has(uid))   // isolate gates
//        AND !hidden.has(uid)                       // hide gates
//    Selection / highlight does NOT touch hidden or isolated. The
//    legacy "toggleIsolate; toggleIsolate;" trick to refresh
//    isolate on selection change is no longer necessary.
//
// === STATE LAYERS ===
//
//   1. visibility   — Set<uid>   (presence == hidden)
//   2. isolation    — Set<uid> | null  (null == not isolating)
//   3. highlight    — Map<uid, Color>
//   4. groups       — Map<groupId, Set<uid>>  (which uids are
//                     hidden BECAUSE of which group toggle)
//
// The `hidden` set is the UNION of all group-contributed uids plus
// individually-hidden uids. Groups exist so the UI can reverse a
// group toggle without losing other groups' contributions.
//
// === BATCHING ===
//
// Subscribers are notified via _emit. Without batching, a 10k-uid
// hide would emit 10k events → 10k applicator runs → death.
//
// All bulk operations (bulkHide, hideGroup, etc.) emit ONCE with
// the full uids array. Subscribers receive { type, uids } where
// uids is the complete change set.
//
// For composite operations across multiple store calls, use:
//   store.beginBatch();
//   store.hide(uid1);
//   store.hide(uid2);
//   store.highlight(uid3, color);
//   store.endBatch();
// During a batch, individual emits are buffered. endBatch() emits
// once per (type) with merged uid lists.

import { Color } from 'three';
import { asFragmentRule } from './isolationRule';
import { parentOf } from './nestedParts';

// A rule may arrive as the CLASS or as the plain object the resolver sends
// over the wire. Both setters normalise through here.
//
// THE BUG THIS EXISTS TO END: setIsolationRule constructed the class and
// setHighlightRule did not, so a caller passing one plain object to both -
// which is exactly what the tracking page does, and exactly the contract
// asked for - got a working isolate and a TypeError on every slot, every
// frame, because getHighlight calls .has() on it. My tests passed the
// class; the caller passed the object; we were each right about our own
// half and the defect lived on the seam. An asymmetry between two setters
// of the same thing IS the bug, not either caller.
//
// The SHAPE half now lives in isolationRule.asFragmentRule, because fixing
// it only here left every consumer outside the store exposed - and the
// framing path, which never touches the store, then threw the same
// TypeError at the owner hours later. What stays here is the store's own
// POLICY, which is not shared: for the store, a rule holding nothing means
// "no isolation / no highlight" (null), never "isolate nothing" - an empty
// FragmentIsolation in `isolated` would hide the entire model.
function asRule(rule) {
  const built = asFragmentRule(rule);
  if (!built) return null;
  return built.size ? built : null;
}

export default class ViewerStateStore {
  constructor() {
    this.hidden = new Set();              // Set<uid>  effective hidden union
    this.isolated = null;                 // Set<uid> | null
    this.highlights = new Map();          // Map<uid, Color>
    this.highlightRule = null;            // FragmentIsolation | null - one colour
    this.hiddenRule = null;               // FragmentIsolation | null
    this.highlightRuleColor = null;

    // === DISABLED-BY-EXPORTER OVERRIDE ===
    //
    // The C# pipeline marks some instances as `disabled` in the
    // sidecar — historically these were rendered invisible
    // unconditionally and there was no way for the user to bring
    // them back. The current product requirement is:
    //
    //   download every disabled GLB at load time (already true —
    //   sidecars include them and pool slots get acquired), keep
    //   them visually disabled until the user explicitly enables.
    //
    // `disabledShown` is the per-uid OVERRIDE set. If a uid has
    // exporter-disabled=true AND lives in disabledShown, it renders.
    // The applicator's per-instance visible check ANDs in this set:
    //
    //   visible = (!disabledByExporter || disabledShown.has(uid))
    //          && !hidden.has(uid)
    //          && (isolated == null || isolated.has(uid))
    //
    // User-facing API surfaces (showDisabled / hideDisabled /
    // bulkShowDisabled / showAllDisabled, etc.) emit 'show' or 'hide'
    // events the applicator already subscribes to — so toggling
    // these re-runs the same code path as a regular hide/show, with
    // the override now in play.
    this.disabledShown = new Set();

    // === DISABLED BUCKET (user-driven ghosting) ===
    //
    // Distinct from `disabledShown` (which overrides the EXPORTER's
    // disabled flag). `disabled` is a USER-driven bucket: when a uid
    // is in this set the applicator renders it as "ghosted" — still
    // visible (subject to the composition precedence) but obviously
    // inert (gray tint via instanceColor). This is the fourth bucket
    // in the precedence:
    //
    //   hidden > isolated-out > disabled > highlight > normal
    //
    // Membership semantics: a uid is "disabled" iff it is in this
    // Set. Adding/removing emits 'disable' / 'enable' events that
    // the applicator subscribes to (re-applies state for affected
    // uids — same code path as hide/show).
    this.disabled = new Set();
    // GHOST BY DEFAULT: every uid that carries no highlight renders
    // ghosted, as if it were in `disabled`, without enumerating it. The
    // construction-tracking stage uses it so a million untracked elements
    // read as grey shells and only the ones with a status stand in colour
    // - enumerating "everything but these" per uid would not scale.
    this.ghostByDefault = false;

    // Groups: each group is a name (treeNodeId, sectionId, etc.) and
    // a set of uids that the group "owns" in the hidden state. UI
    // reads isGroupHidden(groupId) for eye icons.
    this._hiddenGroups = new Map();       // Map<groupId, Set<uid>>

    // WHOLE FILES, hidden by the UniqueId prefix they carry (a Revit
    // element's UniqueId opens with its document's 36-character GUID). The
    // eye on a file's root row in Project Files: a linked file can hold a
    // million elements, so it is hidden as a rule, never as a list, and
    // pieces that stream in later come in hidden too (owner, 2026-09-22:
    // "we should be able to hide a whole linked file").
    this._hiddenDocGroups = new Map();    // Map<groupId, Set<prefix>>
    this._hiddenPrefixes = new Set();     // union of the above

    // HIDDEN BY A PERSON, not derived from a status, a set or a filter.
    // Team rule: a deliberate act outranks a derived one, so bulkShow
    // will not reveal these and only showAll (Unhide all) clears them.
    // Kept BESIDE `hidden` rather than turning it into a Map, because
    // `hidden` is read on the hot path by isHidden for every element and
    // must stay a plain Set lookup.
    this._handHidden = new Set();         // Set<uid>

    this._subscribers = new Set();        // Set<callback>

    // Batching state. _batchDepth allows nested begin/end without
    // losing the outermost emit.
    this._batchDepth = 0;
    this._batched = null;                 // Map<type, Set<uid> | 'full-sweep'>
  }

  // ============================================================
  // Subscription
  // ============================================================

  subscribe(callback) {
    this._subscribers.add(callback);
    return () => this._subscribers.delete(callback);
  }

  _emit(type, uids) {
    if (this._batchDepth > 0) {
      // Buffer for endBatch.
      const existing = this._batched.get(type);
      if (uids === null) {
        // Full-sweep marker. Promote whatever was buffered.
        this._batched.set(type, 'full-sweep');
      } else if (existing === 'full-sweep') {
        // Already a full-sweep; nothing to merge.
      } else if (existing) {
        for (const u of uids) existing.add(u);
      } else {
        this._batched.set(type, new Set(uids));
      }
      return;
    }

    // Snapshot subscribers so callbacks unsubscribing mid-emit don't
    // iterate over a mutating set.
    const snapshot = [...this._subscribers];
    for (const cb of snapshot) {
      try { cb({ type, uids }); }
      catch (err) { console.error('[ViewerStateStore] subscriber threw:', err); }
    }
  }

  // ============================================================
  // Batching
  // ============================================================

  beginBatch() {
    if (this._batchDepth === 0) this._batched = new Map();
    this._batchDepth++;
  }

  endBatch() {
    if (this._batchDepth === 0) {
      console.warn('[ViewerStateStore] endBatch with no active batch');
      return;
    }
    this._batchDepth--;
    if (this._batchDepth > 0) return;

    // Flush. Snapshot the buffer first so any emits triggered by
    // subscribers themselves (re-entrancy) don't see the buffer.
    const buffered = this._batched;
    this._batched = null;

    for (const [type, payload] of buffered) {
      if (payload === 'full-sweep') {
        this._emit(type, null);
      } else if (payload.size > 0) {
        this._emit(type, [...payload]);
      }
    }
  }

  // Convenience helper: fn runs inside a batch.
  withBatch(fn) {
    this.beginBatch();
    try { fn(); }
    finally { this.endBatch(); }
  }

  // ============================================================
  // Hide / show — individual
  // ============================================================

  hide(uid) {
    if (this.hidden.has(uid)) return;
    this.hidden.add(uid);
    this._emit('hide', [uid]);
  }

  show(uid) {
    if (!this.hidden.has(uid)) return;
    // If this uid is contributed by any group, leaving the group(s)
    // intact would re-add it. Pure show() removes it from individual
    // hides AND yanks it out of every group set.
    this.hidden.delete(uid);
    for (const set of this._hiddenGroups.values()) set.delete(uid);
    this._emit('show', [uid]);
  }

  // Hide a RULE rather than a list - the THIRD time tonight a concept
  // turned out to be list-shaped after the selection learned to be a rule
  // (isolate, then highlight and framing, now hide). Hiding a whole
  // category enumerated a million ids for the same reason the others did.
  //
  // Hide is TOP precedence and stays so: hidden beats isolated-out, beats
  // ghosted, beats highlight. A rule does not change that ordering, only
  // how the question is answered.
  setHiddenRule(rule) {
    // WHAT THIS ACCEPTS IS "something that can answer has(uid, hash)", not
    // "a FragmentIsolation". It used to test for `hashes || singles`, which is
    // how a FragmentIsolation happens to be shaped - so the filter-visibility
    // rule (a NEGATION, which has neither: loader/streaming/filterVisibility)
    // was SILENTLY DROPPED and the setter reported nothing. In a browser that
    // reads exactly like "the toggle does nothing", which is the report that
    // sent me looking. A rule is defined by the question it can answer.
    const next = (rule && typeof rule.has === 'function') ? rule : null;
    this.hiddenRule = next;
    // The visible set flips wholesale and cannot be enumerated.
    this._emit(next ? 'hide' : 'show', null);
  }

  clearHiddenRule() {
    if (!this.hiddenRule) return;
    this.hiddenRule = null;
    this._emit('show', null);
  }

  // `hash` is the fragment hash of the pool the element lives in, when the
  // caller knows it. The list is asked first - an individually hidden
  // element stays hidden whatever a rule says.
  isHidden(uid, hash) {
    if (this._isHiddenOwn(uid, hash)) return true;
    // A PART IS HIDDEN WITH ITS ELEMENT (./nestedParts). A hide expands to
    // the family it KNOWS (expandToFamily), and a status hide on the
    // tracking page names only the element - so a hanger's rods in a tile
    // that streamed in afterwards were drawn while the hanger was hidden.
    // Asked only when something is hidden at all, and only for a uid that
    // is not hidden itself: one Map lookup, null for an ordinary element.
    if (!this.hidden.size && !this.hiddenRule && !this._hiddenPrefixes.size) return false;
    const parent = parentOf(uid);
    return !!parent && this._isHiddenOwn(parent);
  }

  _isHiddenOwn(uid, hash) {
    if (this.hidden.has(uid)) return true;
    if (this._hiddenPrefixes.size && typeof uid === 'string' && uid.length >= 36
        && this._hiddenPrefixes.has(uid.slice(0, 36))) return true;
    return !!(this.hiddenRule && this.hiddenRule.has(uid, hash));
  }

  // ============================================================
  // Hide / show — bulk (single emit)
  // ============================================================

  // `byHand` marks a hide as A PERSON'S ACT rather than something derived
  // from a status, a set or a filter. See _handHidden.
  bulkHide(uids, { byHand = false } = {}) {
    const changed = [];
    for (const uid of uids) {
      if (byHand) this._handHidden.add(uid);
      if (!this.hidden.has(uid)) {
        this.hidden.add(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('hide', changed);
  }

  // A DERIVED SHOW REFUSES TO REVEAL WHAT A PERSON HID BY HAND.
  //
  // THE RULE LIVES HERE, NOT AT THE CALL SITE. The tracking legend
  // unhides a status with bulkShow(thoseUids). If it had to filter the
  // hand-hidden out itself it would be correct, and the NEXT feature to
  // call bulkShow would be wrong by default - with no test to catch it,
  // because no test would know hand-hiding exists. Refusing here makes
  // every present and future caller right without knowing the rule.
  //
  // `byHand` is the symmetric case: an explicit unhide of a selection
  // clears the mark, so a hand hide stays reversible by hand.
  bulkShow(uids, { byHand = false } = {}) {
    const changed = [];
    for (const uid of uids) {
      if (byHand) this._handHidden.delete(uid);
      else if (this._handHidden.has(uid)) continue;   // deliberate beats derived
      if (this.hidden.has(uid)) {
        this.hidden.delete(uid);
        for (const set of this._hiddenGroups.values()) set.delete(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('show', changed);
  }

  // Provenance, for a UI hint only. NOTHING depends on a caller asking -
  // if it did, this would be the per-call-site rule the design avoids.
  isHandHidden(uid) { return this._handHidden.has(uid); }

  showAll() {
    if (this._hiddenDocGroups.size) {
      this._hiddenDocGroups.clear();
      this._hiddenPrefixes.clear();
      this._emit('show', null);
    }
    // Unhide all is THE WAY BACK, so it clears provenance too - this is
    // the one path that may reveal a hand-hidden element, and it does so
    // because a person asked for exactly that.
    this._handHidden.clear();
    if (this.hidden.size === 0 && this._hiddenGroups.size === 0) return;
    const all = [...this.hidden];
    this.hidden.clear();
    this._hiddenGroups.clear();
    if (all.length > 0) this._emit('show', all);
  }

  // ============================================================
  // Hide / show — GROUPS (the fast path for tree section toggles)
  // ============================================================

  // Hide a named group of uids. The group is remembered so UI eye
  // icons can ask "is this group hidden". Calling hideGroup with a
  // groupId that already exists merges (union) the new uids in.
  hideGroup(groupId, uids) {
    let groupSet = this._hiddenGroups.get(groupId);
    if (!groupSet) {
      groupSet = new Set();
      this._hiddenGroups.set(groupId, groupSet);
    }

    const changed = [];
    for (const uid of uids) {
      groupSet.add(uid);
      if (!this.hidden.has(uid)) {
        this.hidden.add(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('hide', changed);
  }

  // Restore a group. uids in the group are removed from hidden ONLY
  // if no other group still owns them, and they aren't individually
  // hidden via direct hide(uid) outside any group.
  //
  // (We don't track "individual hide vs group hide" separately —
  // hidden is a flat union. Restoring a group is "remove uids that
  // are owned ONLY by this group AND not owned by any other group".)
  showGroup(groupId) {
    if (this._hiddenDocGroups.has(groupId)) this.showDocument(groupId);
    const groupSet = this._hiddenGroups.get(groupId);
    if (!groupSet) return;

    // Build the set of uids still hidden by OTHER groups.
    const stillHiddenByOthers = new Set();
    for (const [otherId, otherSet] of this._hiddenGroups) {
      if (otherId === groupId) continue;
      for (const uid of otherSet) stillHiddenByOthers.add(uid);
    }

    const changed = [];
    for (const uid of groupSet) {
      if (stillHiddenByOthers.has(uid)) continue;  // another group still hides it
      if (this.hidden.delete(uid)) changed.push(uid);
    }

    this._hiddenGroups.delete(groupId);
    if (changed.length > 0) this._emit('show', changed);
  }

  // For UI eye-icon: O(1) lookup whether a group is in the hidden
  // state. The UI doesn't care WHICH uids — it just renders the
  // closed-eye icon.
  isGroupHidden(groupId) {
    return this._hiddenGroups.has(groupId) || this._hiddenDocGroups.has(groupId);
  }

  // Hide every element whose UniqueId starts with one of `prefixes` - a
  // whole file. Remembered under groupId so the row's eye reads closed and
  // showGroup(groupId) brings the file back. One full sweep: the elements
  // cannot be (and need not be) enumerated.
  hideDocument(groupId, prefixes) {
    const list = [...(prefixes || [])].map(String).filter((p) => p.length === 36);
    if (!list.length) return false;
    this._hiddenDocGroups.set(groupId, new Set(list));
    this._rebuildHiddenPrefixes();
    this._emit('hide', null);
    return true;
  }

  showDocument(groupId) {
    if (!this._hiddenDocGroups.delete(groupId)) return;
    this._rebuildHiddenPrefixes();
    this._emit('show', null);
  }

  isDocumentHidden(groupId) { return this._hiddenDocGroups.has(groupId); }

  _rebuildHiddenPrefixes() {
    this._hiddenPrefixes = new Set();
    for (const set of this._hiddenDocGroups.values()) for (const p of set) this._hiddenPrefixes.add(p);
  }

  // For UI: enumerate all hidden group IDs (rare; debugging).
  getHiddenGroupIds() {
    return [...this._hiddenGroups.keys()];
  }

  // ============================================================
  // Disabled-by-exporter override (see disabledShown field doc)
  // ============================================================

  // Reveal a single exporter-disabled uid. No-op for uids that
  // weren't disabled in the first place — the visibility check
  // ANDs the override with `entry.disabled`, so adding a normal
  // uid here costs only a Set entry and changes no rendering.
  showDisabled(uid) {
    if (this.disabledShown.has(uid)) return;
    this.disabledShown.add(uid);
    // Reuse the 'show' event type — the applicator subscriber
    // already runs applyForUids on 'show', which re-evaluates each
    // affected slot's visible composition (including the new
    // override). One emit, one applicator pass.
    this._emit('show', [uid]);
  }

  hideDisabled(uid) {
    if (!this.disabledShown.has(uid)) return;
    this.disabledShown.delete(uid);
    this._emit('hide', [uid]);
  }

  isDisabledShown(uid) { return this.disabledShown.has(uid); }

  bulkShowDisabled(uids) {
    const changed = [];
    for (const uid of uids) {
      if (!this.disabledShown.has(uid)) {
        this.disabledShown.add(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('show', changed);
  }

  bulkHideDisabled(uids) {
    const changed = [];
    for (const uid of uids) {
      if (this.disabledShown.has(uid)) {
        this.disabledShown.delete(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('hide', changed);
  }

  // ============================================================
  // Disabled bucket (user-driven ghosting) — see field doc above.
  //
  // Membership in `this.disabled` marks a uid for "ghosted" rendering
  // (the fourth bucket: hidden > isolated-out > disabled > highlight).
  // These methods emit 'disable' / 'enable' so the applicator can
  // re-apply state. NOT to be confused with the disabledShown override
  // (which controls the exporter-disabled flag).
  // ============================================================

  disable(uid) {
    if (this.disabled.has(uid)) return;
    this.disabled.add(uid);
    this._emit('disable', [uid]);
  }

  enable(uid) {
    if (!this.disabled.has(uid)) return;
    this.disabled.delete(uid);
    this._emit('enable', [uid]);
  }

  // A part is ghosted with its element: the bucket is filled with the
  // family it knew at the time (expandToFamily), and a part that streams in
  // later must still arrive ghosted. Asked by the applicator per slot, so
  // the parent is looked up only while the bucket holds anything.
  isDisabled(uid) {
    if (this.disabled.has(uid)) return true;
    if (!this.disabled.size) return false;
    const parent = parentOf(uid);
    return !!parent && this.disabled.has(parent);
  }

  // Flip the default look of every un-highlighted uid (see the field).
  // A full-sweep marker, because the set of affected uids is "everything
  // not highlighted" and cannot be listed cheaply.
  setGhostByDefault(on) {
    const next = !!on;
    if (this.ghostByDefault === next) return;
    this.ghostByDefault = next;
    this._emit(next ? 'disable' : 'enable', null);
  }
  isGhostByDefault() { return this.ghostByDefault; }

  bulkDisable(uids) {
    const changed = [];
    for (const uid of uids) {
      if (!this.disabled.has(uid)) {
        this.disabled.add(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('disable', changed);
  }

  bulkEnable(uids) {
    const changed = [];
    for (const uid of uids) {
      if (this.disabled.has(uid)) {
        this.disabled.delete(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('enable', changed);
  }

  clearDisabled() {
    if (this.disabled.size === 0) return;
    const all = [...this.disabled];
    this.disabled.clear();
    this._emit('enable', all);
  }

  // ============================================================
  // Isolation
  // ============================================================

  // Replace the isolation set entirely. Pass an array of uids to
  // isolate; pass null/empty to clear. Emits a full-sweep marker
  // (uids === null in the event) on transitions because the entire
  // visible-set semantics flip — we can't enumerate "every uid not
  // in the new set" cheaply.
  setIsolation(uids) {
    const next = (uids && uids.length > 0) ? new Set(uids) : null;

    // A RULE CANNOT BE DIFFED AGAINST A LIST. setIsolationRule parks a
    // FragmentIsolation here: it answers has() and size like a Set and is
    // NOT iterable, so the symmetric-difference walk below threw
    // "this.isolated is not iterable" the moment a list-shaped isolation
    // landed on top of a rule - which is what creating a set does
    // (owner, prod crash 2026-09-20, main.47ff5081).
    // Treat it as the wholesale flip it is: we cannot enumerate what a
    // rule stood for, which is exactly the case the transition branch
    // below already exists to handle.
    const wasRule = this.isolated !== null && !(this.isolated instanceof Set);

    const isStateTransition =
      (this.isolated === null && next !== null) ||
      (this.isolated !== null && next === null) ||
      wasRule;

    if (isStateTransition) {
      this.isolated = next;
      this._emit(next ? 'isolate' : 'clear-isolate', null);
      return;
    }

    // Within-isolation diff (isolate-set changed but isolation is
    // still active). Symmetric difference is what changed.
    const changed = new Set();
    if (this.isolated) {
      for (const uid of this.isolated) {
        if (!next || !next.has(uid)) changed.add(uid);
      }
    }
    if (next) {
      for (const uid of next) {
        if (!this.isolated || !this.isolated.has(uid)) changed.add(uid);
      }
    }

    this.isolated = next;
    if (changed.size > 0) this._emit('isolate', [...changed]);
  }

  isIsolating() { return this.isolated !== null; }
  // Pass the element's fragment hash when you have it: a RULE isolation
  // answers for pooled geometry from the fragment, and without one it can
  // only answer for merged elements - so a hash-less call on a rule reads
  // a pooled element as isolated OUT. Nothing calls this today; the
  // argument is here so the next caller does not have to find that out.
  //
  // A PART IS IN THE ISOLATION WHEN ITS ELEMENT IS (./nestedParts). The I key
  // isolates expandToFamily(selection) - the parts known at that moment - and
  // the tracking page can isolate a list of ELEMENTS, which names no part at
  // all. Either way a hanger's rods in a tile that lands afterwards were
  // isolated OUT, so an isolated hanger came back as a few nuts. Asked of the
  // parent only when the uid itself is not in: one Map lookup, null for every
  // ordinary element. The parent is asked without a hash - the part's
  // fragment is not the element's.
  isInIsolation(uid, hash) {
    const isolated = this.isolated;
    if (isolated === null || isolated.has(uid, hash)) return true;
    const parent = parentOf(uid);
    return !!parent && isolated.has(parent);
  }

  isolate(uids) { this.setIsolation(uids); }
  clearIsolation() { this.setIsolation(null); }

  // Isolate by RULE instead of by list.
  //
  // A whole category is millions of ids and a few thousand fragments, so
  // the resolver sends what the row STANDS FOR rather than what it
  // contains. What lands here answers the same `has(uid, hash)` question a
  // Set does, so nothing downstream needs to know which it is holding -
  // that is the whole design, and why there is no second isolate path.
  //
  // Always a full sweep: the visible set flips wholesale and we cannot
  // enumerate "every uid not in the new rule" even in principle, which is
  // exactly the situation setIsolation's own transition branch is for.
  setIsolationRule(rule) {
    // Take it whole. Destructuring a fixed field list here DROPPED
    // excludeUids - so "this category except that one" isolated the whole
    // category, the tree said 369 and the viewer showed 370. Anything the
    // rule class learns to hold must not need a second edit in this file
    // to be allowed through (root-8e found it, 2026-09-20).
    const built = asRule(rule);
    if (!built) return this.setIsolation(null);
    if (!built.size) return this.setIsolation(null);
    this.isolated = built;
    this._emit('isolate', null);
  }

  // ============================================================
  // Highlight
  // ============================================================

  highlight(uid, color) {
    const colorObj = (color instanceof Color) ? color.clone() : new Color(color);
    const existing = this.highlights.get(uid);
    if (existing && existing.equals(colorObj)) return;
    this.highlights.set(uid, colorObj);
    this._emit('highlight', [uid]);
  }

  // entries: Iterable<[uid, color]>
  bulkHighlight(entries) {
    const changed = [];
    for (const [uid, color] of entries) {
      const colorObj = (color instanceof Color) ? color.clone() : new Color(color);
      const existing = this.highlights.get(uid);
      if (!existing || !existing.equals(colorObj)) {
        this.highlights.set(uid, colorObj);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('highlight', changed);
  }

  clearHighlight(uid) {
    if (!this.highlights.has(uid)) return;
    this.highlights.delete(uid);
    this._emit('clear-highlight', [uid]);
  }

  // THE LIST IS REQUIRED, and the bare call is refused rather than
  // clearing everything.
  //
  // This Map holds two different things that look identical to the store:
  // an element's STATUS colour and a SELECTION colour, because the page
  // writes both here. So "clear all highlights" cannot mean what it sounds
  // like - wiping the Map takes the statuses with it, and the elements come
  // back as plain material instead of returning to their status colour.
  // That is the owner's own ruling inverted: "when deselected, the status
  // colour takes effect again".
  //
  // Nothing called it bare when this guard was added; the point is that the
  // dangerous call is the NATURAL one to write, and a comment does not stop
  // anyone writing it. A caller that wants to drop a selection should pass
  // the uids it painted, or - better - use a selection RULE, which never
  // writes into this Map at all and so has nothing to restore.
  bulkClearHighlights(uids) {
    if (!uids) {
      console.warn('[ViewerStateStore] bulkClearHighlights needs the uids to clear. '
        + 'Clearing everything would take the status colours with it and return '
        + 'those elements to plain material. Pass the list, or clear a selection '
        + 'rule with clearHighlightRule().');
      return;
    }
    const changed = [];
    for (const uid of uids) {
      if (this.highlights.has(uid)) {
        this.highlights.delete(uid);
        changed.push(uid);
      }
    }
    if (changed.length > 0) this._emit('clear-highlight', changed);
  }

  // Highlight a RULE rather than a list.
  //
  // Ticking a whole category selects a million elements. The isolation
  // learned to be a rule; the highlight did not, so a rule-shaped tick
  // coloured only its `singles` - 2,595 of 1,049,917 on SpaceX's Electrical
  // Fixtures - which reads as nothing happening at all. Same hash-or-uid
  // test the isolation already does, so no enumeration and one lookup per
  // slot.
  //
  // A RULE CARRIES ONE COLOUR. It can say "this category is
  // selection-green"; it cannot give each of a million elements its own
  // status colour, and it is not meant to. Per-element colours keep coming
  // from the Map.
  //
  // PRECEDENCE: THE RULE WINS over the Map, because the rule IS the
  // selection and a selection sits on top of every status colour. That is
  // already literally what the tracking page does with a list -
  // `want.set(uid, SELECT_COLOR)` overwrites the status it just painted
  // (ModelStage.jsx:664-666).
  //
  // I had this backwards and root-8e caught it. With the Map winning, a
  // LIST-shaped selection would cover the status colours and a RULE-shaped
  // one would not - so ticking a 300-element category paints it green and
  // ticking a 1,000,000-element category leaves the installed ones red.
  // The same tick, two behaviours, decided by which shape the answer came
  // back in. That is the class of bug this whole night has been spent on,
  // and it would arrive as "the highlight does not work on big categories".
  //
  // Whether a status colour SHOULD stay visible under a selection is a real
  // question, but it has to be answered the same way for both shapes and it
  // belongs to the owner, not to a precedence rule written at 1 a.m.
  setHighlightRule(rule, color) {
    const next = asRule(rule);
    this.highlightRule = next;
    this.highlightRuleColor = next
      ? ((color instanceof Color) ? color.clone() : new Color(color || 0xffffff))
      : null;
    // The visible set flips wholesale and cannot be enumerated, which is
    // exactly what the full-sweep marker is for.
    this._emit('highlight', null);
  }

  clearHighlightRule() {
    if (!this.highlightRule) return;
    this.highlightRule = null;
    this.highlightRuleColor = null;
    this._emit('highlight', null);
  }

  // `hash` is the fragment hash of the pool the element lives in, when the
  // caller knows it. The SELECTION RULE is asked first; the per-element Map
  // answers for everything the rule does not claim.
  // A PART WEARS ITS ELEMENT'S COLOUR.
  //
  // A trapeze, a hanger, a panelboard has no instance row of its own: it is
  // drawn as its nested pieces, each piece its own uid (./nestedParts). The
  // page writes a colour per ELEMENT - that is what a status is - so asking
  // only for this uid left every one of them uncoloured, and the owner read
  // that as "I don't see them in the tracking view, only with the full model
  // on" (2026-10-03). On the live BA03 export all 32,408 trapezes and 15,120
  // ceiling struts are counted by the board and NOT ONE has a drawn row under
  // its own uid.
  //
  // RESOLVED ON THE READ, not by writing a colour per part. The page would
  // have had to add up to a million more entries - a Color object each - to
  // say one thing it already said about the element. This costs one Map
  // lookup, and only for a uid that has no colour of its own.
  //
  // It is also the one place that serves every consumer: the paint, the
  // ghost decision in InstanceStateApplicator.composeSlotState (a part of a
  // tracked element is no longer ghosted away), and the selection tint.
  getHighlight(uid, hash) {
    // Nothing coloured at all - the usual state while a model streams in:
    // no rule, no Map entry, so no part can inherit one either. Answered
    // without the two lookups (this runs once per slot in every sweep).
    if (!this.highlightRule && this.highlights.size === 0) return null;
    if (this.highlightRule && this.highlightRule.has(uid, hash)) return this.highlightRuleColor;
    const own = this.highlights.get(uid);
    if (own) return own;
    const parent = parentOf(uid);
    if (!parent) return null;
    // The selection RULE answers for the element too, before the element's
    // status colour - the same precedence as for the uid itself. A rule
    // that names the hanger (in `singles`) never names its parts, whose
    // fragments are their own; asked without a hash for the same reason.
    if (this.highlightRule && this.highlightRule.has(parent)) return this.highlightRuleColor;
    return this.highlights.get(parent) || null;
  }

  // ============================================================
  // Composed reads (used by applicator + viewer fit-to-view)
  // ============================================================

  // Effective visibility ignoring entry.disabled. Composition rule:
  // isolate gates first, then hide.
  isEffectivelyVisible(uid, hash) {
    if (!this.isInIsolation(uid, hash)) return false;
    if (this.isHidden(uid, hash)) return false;
    return true;
  }

  // ============================================================
  // Diagnostics
  // ============================================================

  stats() {
    return {
      hiddenCount: this.hidden.size,
      hiddenGroupCount: this._hiddenGroups.size,
      isolating: this.isolated !== null,
      isolatedCount: this.isolated ? this.isolated.size : 0,
      highlightedCount: this.highlights.size,
      disabledCount: this.disabled.size,
      subscriberCount: this._subscribers.size,
      batchDepth: this._batchDepth,
    };
  }
}